import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * UI density — three levels. CSS rules that actually apply the density live
 * in styles/tokens.css (they override .card/.metric/.tbl/.side/.btn padding
 * and row heights via :root[data-density="..."] selectors).
 */
export type Density = 'compact' | 'comfortable' | 'cozy';

export const DENSITIES: readonly Density[] = ['compact', 'comfortable', 'cozy'] as const;

export const DENSITY_LABELS: Record<Density, { name: string; hint: string }> = {
  compact:     { name: 'Compacte',    hint: 'Dense · laboratoire' },
  comfortable: { name: 'Normale',     hint: 'Par défaut' },
  cozy:        { name: 'Confortable', hint: 'Espacée · présentation' },
};

interface DensityState {
  density: Density;
  setDensity: (d: Density) => void;
}

function applyDensity(d: Density): void {
  if (typeof document === 'undefined') return;
  if (d === 'comfortable') {
    document.documentElement.removeAttribute('data-density');
  } else {
    document.documentElement.setAttribute('data-density', d);
  }
}

export const useDensityStore = create<DensityState>()(
  persist(
    (set) => ({
      density: 'comfortable',
      setDensity: (d) => {
        applyDensity(d);
        set({ density: d });
      },
    }),
    {
      name: 'lab.density',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        if (state) applyDensity(state.density);
      },
    },
  ),
);
